/**
 * Shared per-geo visibility toggle for the salience charts (the salience-page
 * ShareTrendChart and the home cross-signal chart).
 *
 * Both charts draw one line per geo (worldwide / UK / US) on a single shared
 * Y axis. Hiding a geo simply drops its series from the render, so recharts —
 * which computes the axis domain only from the series actually present — auto
 * rescales to whatever's left. That's the whole trick: no manual domain math,
 * the "dynamically rescale" behaviour falls out of conditional rendering.
 *
 * Visibility resets whenever the available geo set changes (e.g. switching
 * agenda swaps worldwide/UK/US ↔ worldwide/GB) so a toggle from one agenda
 * never leaks a stale hidden-state into another.
 */
import { useEffect, useState } from "react";

export interface UseGeoToggleOptions {
  /** Predicate for which geos start hidden. Default: none (all visible). */
  defaultHidden?: (geo: string) => boolean;
}

export interface GeoToggle {
  /** Geos currently drawn, in the input order. */
  visibleGeos: string[];
  isVisible: (geo: string) => boolean;
  toggle: (geo: string) => void;
  /** True when every geo has been switched off — chart area is empty. */
  allHidden: boolean;
}

export function useGeoToggle(
  geos: string[],
  opts?: UseGeoToggleOptions,
): GeoToggle {
  const key = geos.join(",");
  const initial = () =>
    new Set(geos.filter((g) => opts?.defaultHidden?.(g)));
  const [hidden, setHidden] = useState<Set<string>>(initial);

  // Re-seed hidden-state when the available geos change (agenda switch). Keyed
  // on the geo list only — a user toggle doesn't change `key`, so it survives.
  useEffect(() => {
    setHidden(new Set(geos.filter((g) => opts?.defaultHidden?.(g))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  function toggle(geo: string) {
    setHidden((cur) => {
      const next = new Set(cur);
      if (next.has(geo)) next.delete(geo);
      else next.add(geo);
      return next;
    });
  }

  const visibleGeos = geos.filter((g) => !hidden.has(g));
  return {
    visibleGeos,
    isVisible: (g: string) => !hidden.has(g),
    toggle,
    allHidden: geos.length > 0 && visibleGeos.length === 0,
  };
}
